import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { Form, Modal } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import { ErrorMessage, Formik, FormikProps } from 'formik';
import { KTSVG, toAbsoluteUrl } from 'src/_metronic/helpers';
import { translate } from 'src/locales/i18next/config';
import Translations from 'src/locales/i18next/translations/translation';
import { IVendorReconciliation, useVendorReconciliationFileRemove, useVendorReconciliationFileUpload, useVendorReconciliationReject } from 'src/pages/dashboard/services';
import * as Yup from 'yup';

type Props = {
  show: boolean;
  handleClose: () => void;
  data: IVendorReconciliation;
};

const ObjectionModal: React.FC<Props> = ({ show, handleClose, data }) => {
  const queryClient = useQueryClient();
  const inputRef = useRef<any>(null);
  const t = translate(Translations);
  const translation = 'pages.dashboard.vendorReconciliation.objectionModal';
  const formikRef = React.useRef<FormikProps<any>>(null);
  const [deletingFileKeys, setDeletingFile] = useState<Record<number, boolean>>({});

  const onError = (err: any) => {
    toast.error(err.response?.data?.detail || err.response?.data?.message || err?.message || 'Dosya yüklenirken bir hata oluştu', { autoClose: 2000 });
  };

  const onSuccessFileUpload = (response: any) => {
    const files = formikRef.current?.values?.files || [];
    files.push(response);
    inputRef.current.value = '';
    formikRef.current?.setFieldValue('files', files);
  };

  const onSuccessFileRemove = (response: any) => {};
  const onSuccessReject = (response: any) => {
    dissmissLocation();
    formikRef?.current?.resetForm();
    queryClient.invalidateQueries(['vendor-reconciliation']);
    toast.success('İtirazınız gönderildi, inceleme sağlayacağız.');
  };

  const { mutate: mutateFileUpload, isLoading: isLoadingFileUpload } = useVendorReconciliationFileUpload(onSuccessFileUpload, onError);
  const { mutateAsync: mutateFileRemoveAsync, isLoading: isLoadingFileRemove } = useVendorReconciliationFileRemove(onSuccessFileRemove, onError);
  const { mutateAsync: mutateReject, isLoading: isLoadingReject } = useVendorReconciliationReject(onSuccessReject, onError);

  const initialValues = {
    rejectMessage: '',
    files: [],
  };
  const objectionSchema = Yup.object().shape({
    rejectMessage: Yup.string()
      .min(10, 'Lütfen en az 10 karakter giriniz.')
      .max(1000, ({ value }) => `En fazla 1000 karakter girilebilir. Girdiğiniz karakter sayısı: ${value.length}`)
      .required('Lütfen itiraz sebebini giriniz.'),
  });

  useEffect(() => {
    initMap();
  }, []);

  const dissmissLocation = () => {
    handleClose();
  };

  const initMap = () => {};

  const changeFiles = (e: any) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Dosya boyutu en fazla 10MB olmalıdır', { autoClose: 2000 });
        inputRef.current.value = '';

        return;
      }

      mutateFileUpload({ files: e?.target?.files, vendorReconciliation: data?.id });
    }
  };

  const handleFileDownload = (url: string) => {
    var link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteFile = async (file: any, index: number) => {
    setDeletingFile((prevState) => ({
      ...prevState,
      [index]: true,
    }));
    const response = await mutateFileRemoveAsync({ fileUrl: file?.url, vendorReconciliation: data?.id });
    const files = formikRef.current?.values?.files || [];
    const removeFile = files?.filter((e: any) => e.url !== response?.url);
    formikRef.current?.setFieldValue('files', [...removeFile]);
    setDeletingFile((prevState) => ({
      ...prevState,
      [index]: false,
    }));
  };

  const iconUrl = (file: any) => {
    switch (file?.extension) {
      case 'pdf':
        return '/media/svg/files/pdf.svg';
      case 'doc':
        return '/media/svg/files/docx.svg';
      case 'docx':
        return '/media/svg/files/docx.svg';
      case 'xlsx':
        return '/media/svg/files/excel.svg';
      case 'png':
        return file?.url;
      case 'jpeg':
        return file?.url;
      case 'jpg':
        return file?.url;
      default:
        break;
    }
  };

  const getFileNumberByType = (files: any[], currentFile: { extension: string }, currentIndex: number): number => {
    return files.slice(0, currentIndex + 1).filter((file) => file.extension === currentFile.extension).length;
  };

  return (
    <Modal className="modal fade" id="kt_modal_select_location" data-backdrop="static" tabIndex={-1} role="dialog" show={show} aria-hidden="true" onHide={dissmissLocation}>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(false);
          const body = { ...values, fileUrls: values?.files?.map((data: any) => data?.url), vendorReconciliation: data?.id };
          delete body.files;
          mutateReject(body);
        }}
        innerRef={formikRef}
        validationSchema={objectionSchema}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
          <Form onSubmit={handleSubmit} className="form">
            <div className="modal-content">
              <div className="modal-header pb-0">
                <div className="fw-bolder fs-4">{t(`${translation}.title`)}</div>

                <div className="btn btn-icon btn-sm btn-active-light-danger mb-3" onClick={dissmissLocation}>
                  <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-2x " />
                </div>
              </div>
              <div className="modal-body">
                <div className="row align-items-center">
                  <div className="col-md-12 col-lg-12 col-sm-12 mb-5">
                    <label className="form-label required">{t(`${translation}.reasonForObjection`)}</label>
                    <textarea
                      name="rejectMessage"
                      rows={5}
                      className={`form-control form-control-solid ${errors?.rejectMessage && touched?.rejectMessage && 'is-invalid border-danger'}`}
                      value={values?.rejectMessage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <p className="text-danger">
                      <ErrorMessage className="text-danger" name="rejectMessage" />
                    </p>
                  </div>
                  <div className="col-md-12 col-lg-12 col-sm-12 mb-5">
                    <label className="form-label ">{t(`${translation}.uploadTitle`)}</label>
                    <input
                      ref={inputRef}
                      accept=".pdf, .xlsx, .docx, .png, .jpeg, .jpg"
                      type="file"
                      multiple={false}
                      disabled={isLoadingFileUpload}
                      className="form-control form-control-solid"
                      onChange={changeFiles}
                    />
                    <span className="fs-8 text-muted">Sadece pdf, xlsx, docx, png ya da jpeg uzantılı dosya yükleyebilirsiniz.</span>
                  </div>
                  <div className="col-md-12 col-lg-12 col-sm-12 mb-5">
                    <div className="d-flex flex-wrap gap-5 justify-content-between">
                      {formikRef.current?.values?.files?.map((file: any, index: number) => (
                        <div className="d-flex flex-column align-items-center justify-content-center bg-light py-7 px-5 rounded" style={{ width: '48%' }}>
                          <div className="symbol symbol-circle bg-white " key={index}>
                            <div className="symbol-label bg-white">
                              <img src={toAbsoluteUrl(iconUrl(file) as string)} className="w-35px" alt="img" />
                            </div>
                          </div>
                          <span className="mt-5 fw-bold fs-7 text-center">
                            {file?.fileName + ' - '}
                            <span>{getFileNumberByType(formikRef.current?.values?.files, file, index)}</span>
                          </span>
                          <div className="d-flex align-items-center justify-content-center gap-3 mt-5">
                            <button className="btn btn-sm btn-light-danger" type="button" onClick={() => handleDeleteFile(file, index)}>
                              {deletingFileKeys[index] ? <div className=" spinner-border w-10px h-10px"></div> : 'Sil'}
                            </button>
                            <button className="btn btn-sm btn-light-primary" type="button" onClick={() => handleFileDownload(file?.url)}>
                              İndir
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-white ">
                <button type="submit" className="btn btn-light-success" disabled={isLoadingReject}>
                  <span className="indicator-label me-3">{t(`${translation}.send`)}</span>

                  {isLoadingReject ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : null}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export { ObjectionModal };
